/** Professional slab grading is independent from the vision-derived condition grade. */

export const PROFESSIONAL_GRADERS = ['PSA', 'BGS', 'SGC', 'CGC', 'TAG', 'HGA'] as const;
export type ProfessionalGrader = (typeof PROFESSIONAL_GRADERS)[number];

export const PROFESSIONAL_GRADES = [
  1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10,
] as const;
export type ProfessionalGrade = (typeof PROFESSIONAL_GRADES)[number];

export const RAW_CARD_CONDITIONS = [
  'NEAR_MINT_OR_BETTER',
  'EXCELLENT',
  'VERY_GOOD',
  'POOR',
] as const;
export type RawCardCondition = (typeof RAW_CARD_CONDITIONS)[number];

export type CardPresentation =
  | { kind: 'RAW'; condition: RawCardCondition }
  | {
      kind: 'GRADED';
      grader: ProfessionalGrader;
      grade: ProfessionalGrade;
      certificationNumber?: string;
    };

export const RAW_CARD_CONDITION_LABELS: Record<RawCardCondition, string> = {
  NEAR_MINT_OR_BETTER: 'Near Mint or Better',
  EXCELLENT: 'Excellent',
  VERY_GOOD: 'Very Good',
  POOR: 'Poor',
};

export const EBAY_PROFESSIONAL_GRADER_IDS: Record<ProfessionalGrader, string> = {
  PSA: '275010',
  BGS: '275013',
  SGC: '275016',
  CGC: '275015',
  TAG: '2750115',
  HGA: '275019',
};

export const EBAY_PROFESSIONAL_GRADE_IDS: Record<ProfessionalGrade, string> = Object.fromEntries(
  [...PROFESSIONAL_GRADES].reverse().map((grade, index) => [grade, `27502${index}`]),
) as Record<ProfessionalGrade, string>;

export const EBAY_RAW_CONDITION_IDS: Record<RawCardCondition, string> = {
  NEAR_MINT_OR_BETTER: '400010',
  EXCELLENT: '400011',
  VERY_GOOD: '400012',
  POOR: '400013',
};

export function normalizeCardPresentation(value: unknown): CardPresentation | undefined {
  if (value === null || typeof value !== 'object') return undefined;
  const candidate = value as Record<string, unknown>;
  if (candidate['kind'] === 'RAW') {
    return RAW_CARD_CONDITIONS.includes(candidate['condition'] as RawCardCondition)
      ? { kind: 'RAW', condition: candidate['condition'] as RawCardCondition }
      : undefined;
  }
  if (candidate['kind'] !== 'GRADED') return undefined;
  const grader = candidate['grader'] as ProfessionalGrader;
  const grade = candidate['grade'] as ProfessionalGrade;
  if (!PROFESSIONAL_GRADERS.includes(grader) || !PROFESSIONAL_GRADES.includes(grade)) {
    return undefined;
  }
  const certificationNumber = candidate['certificationNumber'];
  if (
    certificationNumber !== undefined &&
    (typeof certificationNumber !== 'string' || certificationNumber.trim().length > 30)
  ) {
    return undefined;
  }
  return {
    kind: 'GRADED',
    grader,
    grade,
    ...(typeof certificationNumber === 'string' && certificationNumber.trim() !== ''
      ? { certificationNumber: certificationNumber.trim() }
      : {}),
  };
}

export function cardPresentationLabel(value: CardPresentation | undefined): string {
  if (value === undefined) return 'Unclassified';
  return value.kind === 'RAW'
    ? `Raw · ${RAW_CARD_CONDITION_LABELS[value.condition]}`
    : `${value.grader} ${value.grade}${
        value.certificationNumber === undefined ? '' : ` · Cert ${value.certificationNumber}`
      }`;
}

export function cardPresentationsEqual(
  left: CardPresentation | undefined,
  right: CardPresentation | undefined,
): boolean {
  if (left === undefined || right === undefined) return left === right;
  if (left.kind !== right.kind) return false;
  if (left.kind === 'RAW' && right.kind === 'RAW') return left.condition === right.condition;
  if (left.kind !== 'GRADED' || right.kind !== 'GRADED') return false;
  return (
    left.grader === right.grader &&
    left.grade === right.grade &&
    (left.certificationNumber?.trim() ?? '') === (right.certificationNumber?.trim() ?? '')
  );
}
